import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";

import { AddUserToOrganization } from "../../../services/authorization/Permissions";
import PageLayout from "../../_layout/PageLayout";
import { DataSection, SubSection } from "../../../components/data-components/sections/DataSections";
import EditDescription from "../../../components/data-components/EditDescription";
import DisplayAddedBy from "../../../components/data-components/DisplayAddedBy";
import DisplayDateAdded from "../../../components/data-components/DisplayDateAdded";
import EditEmail from "../../../components/data-components/EditEmail";
import {
  EditUserRole,
  EditEmploymentStatus,
  DisplayFirstName,
  DisplayLastName,
  DisplayPhone,
  DisplayUserName,
} from "../components/DataComponents";
import Form from "../../../components/form/Form";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { UserInviteInput } from "../../../api-models/request/UserInput";
import { HeaderProfile } from "../../../components/svg/svgImageList";
import { EmploymentStatusesStorageModel } from "../../../services/user/EmploymentStatusesStorage";
import { UsersStorageModel } from "../../../services/user/UsersStorage";
import { api } from "../../../services/api/ApiService";
import DisplayImage from "../../../components/data-components/DisplayImage";
import { currentUserService } from "../../../services/user/CurrentUserStorage";
import { UserRolesStorageModel } from "../../../services/user/UserRolesStorage";
import { UserMenuItemsModel } from "../UserMenuItems";
import { EmploymentStatus } from "../../../services/user/EmploymentStatus";
import { warningLeavePageService } from "../../../services/page/WarningLeavePageService";
import { CurrentOrganizationStorageModel } from "../../../services/organization/CurrentOrganizationStorage";

export default observer(() => {
  const history = useHistory();
  const { handleErrorInSnackbar } = useErrorHandler();

  const [usersStorage] = useState(() => UsersStorageModel.create({}, { api }));

  const [employmentStatusesStorage] = useState(() =>
    EmploymentStatusesStorageModel.create({}, { api })
  );

  const [userRolesStorage] = useState(() =>
    UserRolesStorageModel.create({ isDefaultEmployeeStatus: true }, { api })
  );

  const [currentOrganizationStorage] = useState(() =>
    CurrentOrganizationStorageModel.create({}, { api })
  );

  const menuItems = useMemo(() => {
    const userMenuItems = UserMenuItemsModel.create({
      createMode: true,
      organizationHasStripeCustomer: currentOrganizationStorage.organization.hasStripeCustomer,
    });

    return {
      staticItems: userMenuItems.getStaticItems(),
    };
  }, [currentOrganizationStorage.organization.hasStripeCustomer]);

  const getInitialValues = useCallback(() => {
    return {
      email: "",
      role: userRolesStorage.userRoles.find((role: any) => role.default)?.id,
      employmentStatus:
        employmentStatusesStorage.employmentStatuses &&
        employmentStatusesStorage.employmentStatuses.find((status) => status.isDefault),
      description: "",
    };
  }, [employmentStatusesStorage.employmentStatuses, userRolesStorage]);

  const goToUsersList = useCallback(() => history.push("/users"), [history]);

  const onChangeEmploymentStatus = useCallback(
    (employmentStatusType: EmploymentStatus, setFieldValue: Function) => {
      if (employmentStatusType.isDefault) {
        userRolesStorage.setIsDefaultEmployeeStatus(true);
        return;
      }
      userRolesStorage.setIsDefaultEmployeeStatus(false);
      setFieldValue("role", userRolesStorage.rolesForCurrentEmploymentStatus[0].id);
    },
    [userRolesStorage]
  );

  const handleSubmit = async (values: any, formActions: any) => {
    const user: UserInviteInput = {
      email: values.email,
      roleId: Number(values.role),
      employmentStatusId: values.employmentStatus.id,
      comments: values.description,
    };
    try {
      await usersStorage.inviteUser(user);
      warningLeavePageService.setIsDirty(false);
      return goToUsersList();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withForm(values, formActions);
      applicationError.withErrorMessage("default-error.user.invite");
      handleErrorInSnackbar(applicationError);
    }
  };

  return (
    <PageLayout
      accessWithOneOfThePermissions={[AddUserToOrganization]}
      titleKey="InviteUser.title"
      isLoading={employmentStatusesStorage.isLoading || userRolesStorage.isLoading}
      menuItems={menuItems}
    >
      <Form
        formikOptions={{
          initialValues: getInitialValues(),
          onSubmit: handleSubmit,
          validateOnBlur: false,
          validateOnChange: false,
          enableReinitialize: true,
        }}
        onCancel={goToUsersList}
      >
        {({ values, setFieldValue }) => {
          return (
            <>
              <DataSection labelKey="general.users.details">
                <SubSection>
                  <DisplayImage id="user-image" placeholderImage={HeaderProfile} />
                </SubSection>

                <SubSection>
                  <EditEmail email={values.email} />
                  <EditEmploymentStatus
                    employmentStatus={values.employmentStatus}
                    setFieldValue={setFieldValue}
                    options={employmentStatusesStorage.employmentStatuses}
                    onChange={(employmentStatusType: EmploymentStatus) => {
                      onChangeEmploymentStatus(employmentStatusType, setFieldValue);
                    }}
                    organizationName={currentUserService.currentUser.currentOrganization.name}
                  />
                  <EditUserRole
                    role={values.role}
                    roleOptions={userRolesStorage.rolesForCurrentEmploymentStatus}
                    setFieldValue={setFieldValue}
                  />
                </SubSection>

                <SubSection>
                  <DisplayUserName userName="-" />
                  <DisplayFirstName name="-" />
                  <DisplayLastName name="-" />
                  <DisplayPhone phone="-" />
                </SubSection>

                <SubSection>
                  <DisplayDateAdded />
                  <DisplayAddedBy />
                </SubSection>
              </DataSection>

              <DataSection labelKey="general.comments">
                <EditDescription
                  description={values.description}
                  labelKey="general.comments"
                  placeholderKey="EditComment.label"
                />
              </DataSection>
            </>
          );
        }}
      </Form>
    </PageLayout>
  );
});
